import React, { Component } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Popover,
  Progress,
  Row,
  Space,
  Modal,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { InfoCircleOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import moment from "moment";
import { PmsAPI } from "../../../controller/PmsAPI";
import ExposureSchedule from "./ExposureSchedule";
import { FONT } from "../../styles/typography";

const { Paragraph, Text, Title } = Typography;
const DATE_FMT = "YYYY/MM/DD";

// 期間粒度。通路統計用月/季/年，這裡改成日/月/年 —— 曝光配置是逐日調整的，
// 日粒度要能單獨看某一天；季在這個情境沒有對應的作業節奏。
const PRESETS = [
  { unit: "day", label: "日" },
  { unit: "month", label: "月" },
  { unit: "year", label: "年" },
];

// 滾動區間 —— 與上面的日曆單位不同，它不對齊月初月底，而是自今天往後推。
// 日曆單位回答「這個月配置得如何」，滾動區間回答「接下來這一段配置得如何」，
// 後者才是可調控的範圍，跨月檢視待補的配額時比較自然。
const ROLLING_PRESETS = [
  { key: "next3m", label: "未來三個月", months: 3 },
];

const CALENDAR_UNITS = PRESETS.map((p) => p.unit);

// 預設區間 —— 這頁的重點是「接下來還能怎麼調」，落在未來比落在本月自然
const DEFAULT_PRESET = ROLLING_PRESETS[0];

const DANGER = "#ff4d4f";
const WARN = "#d46b08";
const OK = "#52c41a";

export class ExposurePage extends Component {
  state = {
    loading: false,
    error: null,
    granularity: DEFAULT_PRESET.key,
    anchor: moment(),
    from: moment(),
    to: moment().add(DEFAULT_PRESET.months, "months").subtract(1, "day"),
    rows: [],
    totals: null,
    channel: null,
    // 曝光排程自成一格：固定從今天起算，與上方的期間選擇無關，
    // 故獨立查詢、獨立處理載入與錯誤 —— 一邊掛掉不該讓另一邊也不能用
    scheduleMonths: 6,
    schedule: { data: null, loading: false, error: null },
    // 點「零配額天數」後要看的房型 (null = 未開啟)
    zeroDetail: null,
  };

  // 遞增序號：快速切換期間時，丟棄較舊的回應 (同 ChannelPage 的做法)
  _seq = 0;
  _scheduleSeq = 0;

  componentDidMount() {
    this.load();
    // 排程分頁一併在初次載入時取得 —— 使用者切過去時不必再等一次查詢
    this.loadSchedule();
  }

  loadSchedule = (months = this.state.scheduleMonths) => {
    const seq = ++this._scheduleSeq;
    this.setState((s) => ({ schedule: { ...s.schedule, loading: true, error: null } }));
    PmsAPI.exposure
      .schedule(months)
      .then((res) => {
        if (seq !== this._scheduleSeq) return;
        this.setState({ schedule: { data: res.data || null, loading: false, error: null } });
      })
      .catch((e) => {
        if (seq !== this._scheduleSeq) return;
        this.setState({
          schedule: {
            data: null,
            loading: false,
            error: e?.response?.data?.message || "查詢失敗，請稍後重試",
          },
        });
      });
  };

  // setState 後 scheduleMonths 尚未更新，故把新值直接傳進去
  handleScheduleMonthsChange = (months) => {
    this.setState({ scheduleMonths: months });
    this.loadSchedule(months);
  };

  // 日/月/年都取完整期間 —— 曝光看的是「這段期間的配置長什麼樣」，
  // 不像營收需要「期初至今」才能跟上期公平比較。
  periodRange = (unit, anchor) => ({
    from: anchor.clone().startOf(unit),
    to: anchor.clone().endOf(unit),
  });

  applyCalendar = (unit, anchor) => {
    this.setState({ granularity: unit, anchor, ...this.periodRange(unit, anchor) }, this.load);
  };

  setPreset = (unit) => this.applyCalendar(unit, moment());

  /** 滾動區間：自今天往後推 N 個月 (含今天) */
  setRolling = (preset) => {
    const from = moment();
    const to = moment().add(preset.months, "months").subtract(1, "day");
    this.setState({ granularity: preset.key, anchor: from.clone(), from, to }, this.load);
  };

  navigate = (dir) => {
    const { granularity, anchor, from, to } = this.state;
    // 非日曆單位 (自訂區間、滾動區間) 一律以區間長度滑動
    if (!CALENDAR_UNITS.includes(granularity)) {
      const len = to.diff(from, "days") + 1;
      this.setState(
        { from: from.clone().add(dir * len, "days"), to: to.clone().add(dir * len, "days") },
        this.load
      );
    } else {
      this.applyCalendar(granularity, anchor.clone().add(dir, granularity));
    }
  };

  onRangeChange = (range) => {
    if (!range || !range[0] || !range[1]) return;
    this.setState(
      { granularity: "custom", anchor: range[0].clone(), from: range[0], to: range[1] },
      this.load
    );
  };

  load = () => {
    const { from, to } = this.state;
    const seq = ++this._seq;
    this.setState({ loading: true, error: null });
    PmsAPI.exposure
      .stat(from.format(DATE_FMT), to.format(DATE_FMT))
      .then((res) => {
        if (seq !== this._seq) return;
        const d = res.data || {};
        this.setState({ rows: d.rows || [], totals: d.totals || null, channel: d.channel || null });
      })
      .catch((e) => {
        if (seq !== this._seq) return;
        this.setState({
          rows: [],
          totals: null,
          channel: null,
          error: e?.response?.data?.message || "查詢失敗，請稍後重試",
        });
      })
      .finally(() => {
        if (seq === this._seq) this.setState({ loading: false });
      });
  };

  /** 選到的期間是完全在過去、還是含今天以後 —— 兩者的判讀方式不同 */
  periodKind = () => {
    const today = moment().startOf("day");
    if (this.state.to.isBefore(today, "day")) return "past";
    if (this.state.from.isAfter(today, "day")) return "future";
    return "mixed";
  };

  columns = () => [
    {
      title: "房型",
      key: "room",
      render: (_, r) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {r.nam}
          <Text type="secondary" style={{ fontSize: FONT.caption, marginLeft: 6 }}>{r.cod}</Text>
        </span>
      ),
    },
    {
      title: "未售",
      dataIndex: "unsold",
      key: "unsold",
      align: "right",
      sorter: (a, b) => a.unsold - b.unsold,
    },
    {
      title: "有線上曝光",
      dataIndex: "exposed",
      key: "exposed",
      align: "right",
      sorter: (a, b) => a.exposed - b.exposed,
    },
    {
      title: "無線上曝光",
      dataIndex: "unexposed",
      key: "unexposed",
      align: "right",
      sorter: (a, b) => a.unexposed - b.unexposed,
      render: (v) => <b style={{ color: v > 0 ? WARN : "inherit" }}>{v}</b>,
    },
    {
      title: (
        <span style={{ whiteSpace: "nowrap" }}>
          無曝光佔比
          <Popover
            content={
              <div style={{ maxWidth: 280 }}>
                以<b>房晚</b>計：未售的房裡有多少比例只有現場賣得到。
                衡量的是「給了 OTA 多少庫存」，與右側的「零配額天數佔比」不同 ——
                後者衡量的是「客人找不找得到」。
              </div>
            }
          >
            <InfoCircleOutlined style={{ color: "#bfbfbf", marginLeft: 4, cursor: "help" }} />
          </Popover>
        </span>
      ),
      dataIndex: "unexposed_pct",
      key: "unexposed_pct",
      align: "right",
      width: 130,
      sorter: (a, b) => a.unexposed_pct - b.unexposed_pct,
      render: (v) => <Text type="secondary">{v}%</Text>,
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>零配額天數</span>,
      dataIndex: "zero_quota_days",
      key: "zero_quota_days",
      align: "right",
      sorter: (a, b) => a.zero_quota_days - b.zero_quota_days,
      // 可點開看是哪幾天 —— 只知道「有 28 天沒配額」無法行動，要知道是哪一段才能去補
      render: (v, r) =>
        v > 0 ? (
          <Tag
            color="orange"
            style={{ cursor: "pointer" }}
            onClick={() => this.setState({ zeroDetail: r })}
          >
            {v} / {r.days} 天
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: (
        <span style={{ whiteSpace: "nowrap" }}>
          零配額天數佔比
          <Popover
            content={
              <div style={{ maxWidth: 300 }}>
                以<b>天數</b>計：該房型有多少比例的日子，在 OTA 上完全搜不到。
                <br />
                OTA 的曝光是逐日逐房型的二元狀態 —— 配額 1 間或 5 間都是「搜得到」，
                只有 <b>0 才是搜不到</b>，所以要看天數而非房晚。
                <br />
                無障礙客房通常是刻意不開放線上訂，屬正常。
              </div>
            }
          >
            <InfoCircleOutlined style={{ color: "#bfbfbf", marginLeft: 4, cursor: "help" }} />
          </Popover>
        </span>
      ),
      dataIndex: "zero_quota_pct",
      key: "zero_quota_pct",
      align: "right",
      width: 180,
      // 預設排序欄位 —— 曝光缺口最嚴重的排前面。antd 同時設多欄的 defaultSortOrder
      // 只會生效一個，故全表僅此一欄設定。
      defaultSortOrder: "descend",
      sorter: (a, b) => a.zero_quota_pct - b.zero_quota_pct,
      render: (v) => (
        <Progress
          percent={v}
          size="small"
          status={v >= 80 ? "exception" : v >= 30 ? "normal" : "success"}
          format={(p) => `${p}%`}
        />
      ),
    },
  ];

  renderSummary() {
    const { totals, channel } = this.state;
    if (!totals) return null;

    const gap = channel ? channel.gap_pct : 0;

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="未售總量 (房晚)" value={totals.unsold} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="有線上曝光 (房晚)"
              value={totals.exposed}
              suffix={<Text type="secondary" style={{  }}>({totals.exposed_pct}%)</Text>}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="無線上曝光 (房晚)"
              value={totals.unexposed}
              valueStyle={{ color: WARN }}
              suffix={<Text type="secondary" style={{  }}>({totals.unexposed_pct}%)</Text>}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={
                <span>
                  配置落差
                  <Popover
                    title="線上成交佔比 − 線上曝光佔比"
                    content={
                      <div style={{ maxWidth: 280 }}>
                        線上通路實際帶來多少比例的生意，對照未售庫存有多少比例曝光在線上。
                        <br />
                        <b>正值</b>代表線上的配額給得比需求少 —— 生意大多從線上來，庫存卻押在現場。
                        <br />
                        <b>負值</b>代表線上給得比需求多。
                        <br />
                        線上通路的認定沿用「設定 → 通路佣金」中有設比對樣式的來源。
                      </div>
                    }
                  >
                    <InfoCircleOutlined style={{ color: "#bfbfbf", marginLeft: 4, cursor: "help" }} />
                  </Popover>
                </span>
              }
              value={gap > 0 ? `+${gap}` : gap}
              suffix="%"
              valueStyle={{ color: gap > 10 ? DANGER : gap > 0 ? WARN : OK }}
            />
            {channel && (
              <Text type="secondary" style={{ fontSize: FONT.caption }}>
                線上成交 {channel.online_pct}%　曝光 {totals.exposed_pct}%
              </Text>
            )}
          </Card>
        </Col>
      </Row>
    );
  }

  render() {
    const { loading, error, granularity, from, to, rows, schedule, scheduleMonths, zeroDetail } = this.state;
    const kind = this.periodKind();

    return (
      <Card>
        <Title level={4}>OTA 曝光分析</Title>

        {/* 這頁的數字不看說明容易被當成一般的空房統計，故把用途與判讀方式常駐在最上方 */}
        <Card size="small" style={{ marginBottom: 16, background: "#fafafa" }}>
          <Paragraph style={{ marginBottom: 8 }}>
            這頁在回答一個問題：<Text strong>還沒賣掉的房，OTA 到底看不看得到？</Text>
          </Paragraph>
          {/* 德安的 WRS 是所有線上通路的共同進單閘道，官網也走這條 (實測佔其中 6.7%)。
              本頁的「OTA」與營運儀表板上「OTA vs 直訂」的 OTA 不同義，不註明會被誤讀。 */}
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            本頁的「OTA」指<Text strong>所有線上通路，飯店官網也包含在內</Text> ——
            德安的 WRS 鎖控是共用的一份，Booking、Agoda、Expedia、C-TRIP、Klook、易遊網與官網
            全部從這裡扣 (實測官網約佔 6.7%)。與營運儀表板上「OTA vs 直訂」的分法不同。
          </Paragraph>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            德安把每個房型每天的未售庫存分成兩塊 ——
            掛在<Text strong>線上銷售</Text>的 OTA 賣得到，其餘的<Text strong>現場可售</Text>
            只有櫃台與直訂碰得到，需請訂房組釋出才會出現在 OTA。
            當生意大多從線上來、庫存卻押在現場，就會白白少賣；
            <Text strong>配置落差</Text>就是在量這件事。
          </Paragraph>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            可以用來找出三件事：
            <Text strong>① 配額給太少的房型</Text> (無曝光佔比高)、
            <Text strong>② 完全沒開放線上訂的日子</Text> (零配額天數)、
            <Text strong>③ 整體配額是否跟得上線上的需求</Text> (配置落差為正代表跟不上)。
            要逐日逐房型處理，到「房型庫存查詢 → 待調控清單」。
          </Paragraph>
        </Card>

        <Tabs defaultActiveKey="structure">
        <Tabs.TabPane tab="曝光結構" key="structure">
        <Space style={{ marginBottom: 16 }} wrap>
          <Button.Group>
            {/* 滾動區間擺最左 —— 它是預設，也是這頁最常用的視角 */}
            {ROLLING_PRESETS.map((p) => (
              <Button
                key={p.key}
                type={granularity === p.key ? "primary" : "default"}
                onClick={() => this.setRolling(p)}
              >
                {p.label}
              </Button>
            ))}
            {PRESETS.map((p) => (
              <Button
                key={p.unit}
                type={granularity === p.unit ? "primary" : "default"}
                onClick={() => this.setPreset(p.unit)}
              >
                {p.label}
              </Button>
            ))}
          </Button.Group>
          <Button.Group>
            <Button icon={<LeftOutlined />} onClick={() => this.navigate(-1)} />
            <Button icon={<RightOutlined />} onClick={() => this.navigate(1)} />
          </Button.Group>
          <DatePicker.RangePicker
            value={[from, to]}
            format={DATE_FMT}
            allowClear={false}
            onChange={this.onRangeChange}
          />
          <Text type="secondary">
            {from.format(DATE_FMT)} ~ {to.format(DATE_FMT)}
          </Text>
        </Space>

        {/* 同一組數字在過去與未來的意義不同，不標註會被讀錯 */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            kind === "past"
              ? "此區間已結束：「未售」是最後沒有賣掉的量，看的是配置的結果"
              : kind === "future"
                ? "此區間尚未到來：「未售」是目前還沒賣掉、仍可調控的量"
                : "此區間橫跨今天：已過去的部分是配置結果，未來的部分仍可調控"
          }
          description="OTA 只賣得到「線上銷售」那一份；「現場可售」的房線上完全看不到，需請訂房組釋出才會出現在 OTA。"
        />

        {error ? (
          <Alert
            type="error"
            showIcon
            message="無法取得 OTA 曝光資料"
            description={error}
            action={<Button size="small" onClick={this.load}>重試</Button>}
          />
        ) : (
          <>
            {this.renderSummary()}
            <Table
              size="small"
              rowKey="cod"
              loading={loading}
              columns={this.columns()}
              dataSource={rows}
              pagination={false}
              // 欄位加到 7 欄後在窄視窗會溢出，沒設 scroll 會直接被裁掉而非可捲動
              scroll={{ x: "max-content" }}
            />
            {/* 跨多天時數字是「間 × 晚」的加總，不標單位會被讀成間數 */}
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              未售、有/無線上曝光的單位皆為<Text strong>房晚</Text> (一間房 × 一晚)，
              為選定期間內所有日期的加總；選「日」粒度時才等同於間數。
            </Text>
          </>
        )}
        </Tabs.TabPane>

        <Tabs.TabPane tab="曝光排程" key="schedule">
          <ExposureSchedule
            data={schedule.data}
            loading={schedule.loading}
            error={schedule.error}
            months={scheduleMonths}
            onMonthsChange={this.handleScheduleMonthsChange}
            onRefresh={() => this.loadSchedule()}
          />
        </Tabs.TabPane>
        </Tabs>

        <Modal
          // antd 4.19 用 visible；open 是 4.23 之後才有，傳了不會報錯但永遠不顯示
          visible={Boolean(zeroDetail)}
          title={
            zeroDetail
              ? `${zeroDetail.nam} (${zeroDetail.cod}) — 沒有線上配額的日期`
              : ""
          }
          onCancel={() => this.setState({ zeroDetail: null })}
          footer={null}
          width={520}
        >
          {zeroDetail && (
            <>
              <Paragraph type="secondary">
                以下日期該房型仍有未售房，但<Text strong>完全沒有線上配額</Text> ——
                這幾天在 OTA 上搜不到這個房型。連續的日期已合併顯示。
              </Paragraph>
              <Table
                size="small"
                rowKey={(r) => `${r.from}:${r.to}`}
                columns={[
                  { title: "起", dataIndex: "from", key: "from" },
                  { title: "迄", dataIndex: "to", key: "to" },
                  { title: "天數", dataIndex: "days", key: "days", align: "right" },
                ]}
                dataSource={zeroDetail.zero_quota_ranges || []}
                pagination={
                  (zeroDetail.zero_quota_ranges || []).length > 10
                    ? { pageSize: 10, showSizeChanger: false }
                    : false
                }
              />
            </>
          )}
        </Modal>
      </Card>
    );
  }
}

export default ExposurePage;
